import { FEATURES, useFeature } from '@/features/feature-flags';
import { subscriptionStore } from '@/features/subscription/subscription.store';
import { useIsInternalUser } from '@/hooks/useIsInternalUser';

import type { MeetingTriggerKind } from '@littlebird/sdk';

/**
 * Whether the hourly / custom / raw-RRULE schedule options are available.
 *
 * Mirrors `can_use_advanced_schedules()` in
 * `backend/lb/features/routines/routines_access.py` — the API rejects an advanced
 * rule from an ineligible user with a 403, so the two must agree or the UI offers
 * a schedule that can't be saved. Both halves must hold: the rollout flag, and a
 * Power/Pro plan (or internal access).
 *
 * Reads MobX state, so callers must be `observer` components.
 */
export function useAdvancedSchedulesEnabled(): boolean {
  const checkFeature = useFeature();
  const isInternalUser = useIsInternalUser();
  return (
    checkFeature(FEATURES.ROUTINE_ADVANCED_SCHEDULES) &&
    (subscriptionStore.isPowerOrPro || isInternalUser)
  );
}

export type RoutineTriggerKindsEnabled = {
  gmail: boolean;
  outlook: boolean;
  webhook: boolean;
  meeting: Record<MeetingTriggerKind, boolean>;
};

/**
 * Which event trigger kinds can be attached to a routine, each behind its own flag.
 *
 * Mirrors `allowed_trigger_kinds()` in
 * `backend/lb/features/routines/routines_access.py`, which rejects a trigger of a
 * kind the user lacks with a 403 — so offering the option here without the flag
 * would surface an error the user can't act on.
 */
export function useRoutineTriggerKindsEnabled(): RoutineTriggerKindsEnabled {
  const checkFeature = useFeature();
  return {
    gmail: checkFeature(FEATURES.ROUTINE_TRIGGERS),
    outlook: checkFeature(FEATURES.ROUTINE_OUTLOOK_TRIGGERS),
    webhook: checkFeature(FEATURES.ROUTINE_WEBHOOK_TRIGGERS),
    meeting: {
      meeting_started: checkFeature(FEATURES.ROUTINE_MEETING_STARTED_TRIGGERS),
      meeting_summarized: checkFeature(
        FEATURES.ROUTINE_MEETING_SUMMARIZED_TRIGGERS
      ),
    },
  };
}
